import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const MOBILE_MAX_PX = 989;
const TABLET_MAX_PX = 1179;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element (single-grid layout).
 * @property {HTMLUListElement} [gridDesktop] - Desktop grid (multi-grid layout, ≥1180px).
 * @property {HTMLUListElement} [gridTablet] - Tablet grid (multi-grid layout, 990–1179px).
 * @property {HTMLUListElement} [gridMobile] - Mobile grid (multi-grid layout, ≤989px).
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  get #visibleGrid() {
    const { grid, gridDesktop, gridTablet, gridMobile } = this.refs;
    if (grid) return grid;
    if (gridTablet) {
      if (window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches) return gridMobile;
      if (window.matchMedia(`(min-width: ${MOBILE_MAX_PX + 1}px) and (max-width: ${TABLET_MAX_PX}px)`).matches) return gridTablet;
      return gridDesktop;
    }
    const isMobile = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
    return isMobile ? gridMobile : gridDesktop;
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const grid = this.#visibleGrid;
    const lastPage = grid?.dataset?.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid, gridDesktop, gridTablet, gridMobile } = this.refs;
    const useMultiGrid = Boolean(gridDesktop && gridMobile);
    const targetGrid = grid ?? (useMultiGrid ? null : this.#visibleGrid);

    if (!targetGrid && !useMultiGrid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let nextPageItemElements = null;
    let gridsContent = null;
    if (useMultiGrid) {
      gridsContent = this.#getGridsContentForPage(nextPage.page);
    } else {
      nextPageItemElements = this.#getGridForPage(nextPage.page);
    }

    if (!nextPageItemElements && !gridsContent) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;
      if (useMultiGrid) {
        gridsContent = this.#getGridsContentForPage(nextPage.page);
      } else {
        nextPageItemElements = this.#getGridForPage(nextPage.page);
      }
      if (!nextPageItemElements && !gridsContent) return;
    }

    if (gridsContent && gridDesktop && gridMobile) {
      gridDesktop.append(...gridsContent.desktop);
      gridMobile.append(...gridsContent.mobile);
      if (gridTablet && gridsContent.tablet) {
        gridTablet.append(...gridsContent.tablet);
      }
    } else if (nextPageItemElements && targetGrid) {
      targetGrid.append(...nextPageItemElements);
    }

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid, gridDesktop, gridTablet, gridMobile } = this.refs;
    const useMultiGrid = Boolean(gridDesktop && gridMobile);
    const targetGrid = grid ?? (useMultiGrid ? null : this.#visibleGrid);

    if (!targetGrid && !useMultiGrid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = null;
    let gridsContent = null;
    if (useMultiGrid) {
      gridsContent = this.#getGridsContentForPage(previousPage.page);
    } else {
      previousPageItemElements = this.#getGridForPage(previousPage.page);
    }

    if (!previousPageItemElements && !gridsContent) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;
      if (useMultiGrid) {
        gridsContent = this.#getGridsContentForPage(previousPage.page);
      } else {
        previousPageItemElements = this.#getGridForPage(previousPage.page);
      }
      if (!previousPageItemElements && !gridsContent) return;
    }

    const gridToMeasure = useMultiGrid ? this.#visibleGrid : targetGrid;
    const scrollTop = window.scrollY;
    const firstElement = gridToMeasure?.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    if (gridsContent && gridDesktop && gridMobile) {
      gridDesktop.prepend(...gridsContent.desktop);
      gridMobile.prepend(...gridsContent.mobile);
      if (gridTablet && gridsContent.tablet) {
        gridTablet.prepend(...gridsContent.tablet);
      }
    } else if (previousPageItemElements && targetGrid) {
      targetGrid.prepend(...previousPageItemElements);
    }

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const grid = this.#visibleGrid;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[ref="cards[]"]'));
    const isPrevious = type === 'previous';
    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const { gridTablet } = this.refs;
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    let gridSelector = '[ref="grid"]';
    if (gridTablet) {
      if (window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches) gridSelector = '[ref="gridMobile"]';
      else if (window.matchMedia(`(min-width: ${MOBILE_MAX_PX + 1}px) and (max-width: ${TABLET_MAX_PX}px)`).matches) gridSelector = '[ref="gridTablet"]';
      else gridSelector = '[ref="gridDesktop"]';
    } else {
      const isMobile = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
      gridSelector = isMobile ? '[ref="gridMobile"]' : '[ref="gridDesktop"]';
    }
    const gridElement = parsedPage.querySelector(gridSelector) ?? parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > li');
  }

  /**
   * When desktop and mobile grids exist, return items for each so they stay in sync.
   * If tablet grid exists, also extract tablet items. Clones when same source is used for multiple grids.
   * @param {number} page
   * @returns {{ desktop: Element[], tablet?: Element[], mobile: Element[] } | undefined}
   */
  #getGridsContentForPage(page) {
    const { gridDesktop, gridTablet, gridMobile } = this.refs;
    if (!gridDesktop || !gridMobile) return;

    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const desktopGrid = parsedPage.querySelector('[ref="gridDesktop"]') ?? parsedPage.querySelector('[ref="grid"]');
    const tabletGrid = parsedPage.querySelector('[ref="gridTablet"]');
    const mobileGrid = parsedPage.querySelector('[ref="gridMobile"]') ?? parsedPage.querySelector('[ref="grid"]');
    if (!desktopGrid || !mobileGrid) return;

    const desktop = Array.from(desktopGrid.querySelectorAll(':scope > li'));
    const mobile = desktopGrid === mobileGrid
      ? desktop.map((el) => el.cloneNode(true))
      : Array.from(mobileGrid.querySelectorAll(':scope > li'));
    let tablet;
    if (gridTablet && tabletGrid) {
      tablet = desktopGrid === tabletGrid
        ? desktop.map((el) => el.cloneNode(true))
        : Array.from(tabletGrid.querySelectorAll(':scope > li'));
    }
    const result = { desktop, mobile };
    if (tablet) result.tablet = tablet;
    return result;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.#visibleGrid?.dataset?.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.#visibleGrid?.dataset?.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const grid = this.#visibleGrid;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
